import React, { useState, useRef, useEffect } from "react";
import { Link } from 'react-router-dom';

const ProductSection = ({
  title,
  subheader,
  specs = [],
  features = [],
  applications,
  contactCta,
  imageUrl = null,
  images = [],
  autoPlay = true,
  autoPlayInterval = 2000,

}) => {

      // if images array empty and imageUrl exists, use single-image array for uniformity
      const imgs = (images && images.length > 0) ? images : (imageUrl ? [imageUrl] : []);
      const [current, setCurrent] = useState(0);
      const autoplayRef = useRef(null);

      // touch handling for swipe
      const touchStartX = useRef(0);
      const touchEndX = useRef(0);

      useEffect(() => {
        if (!autoPlay || imgs.length <= 1) return;

        const startAuto = () => {
          clearInterval(autoplayRef.current);
          autoplayRef.current = setInterval(() => {
            setCurrent(prev => (prev + 1) % imgs.length);
          }, autoPlayInterval);
        };

        startAuto();
        return () => clearInterval(autoplayRef.current);
      }, [autoPlay, autoPlayInterval, imgs.length]);

      const goTo = (index) => {
        setCurrent(index % imgs.length);
      };

      const onMouseEnter = () => {
        if (autoplayRef.current) clearInterval(autoplayRef.current);
      };
      const onMouseLeave = () => {
        if (!autoPlay || imgs.length <= 1) return;
        autoplayRef.current = setInterval(() => {
          setCurrent(prev => (prev + 1) % imgs.length);
        }, autoPlayInterval);
      };

      const onTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
      };
      const onTouchMove = (e) => {
        touchEndX.current = e.touches[0].clientX;
      };
      const onTouchEnd = () => {
        const dx = touchStartX.current - touchEndX.current;
        const swipeThreshold = 40; // px
        if (Math.abs(dx) > swipeThreshold) {
          if (dx > 0) {
            // left swipe -> next
            setCurrent(prev => (prev + 1) % imgs.length);
          } else {
            // right swipe -> prev
            setCurrent(prev => (prev - 1 + imgs.length) % imgs.length);
          }
        }
        touchStartX.current = 0;
        touchEndX.current = 0;
      };
  return (
    <div className="mt-16 p-6 md:p-10 bg-white text-gray-800 max-w-7xl mx-auto" 
    >

      {/* Header */}
      <h1 className="text-center text-3xl font-bold text-gray-800" 
      >
        {title}
      </h1>

      {/* Subheader */}
      <p className="text-lg mb-5 mt-2 text-center">
        {subheader}
      </p>

      {/* Image / Carousel */}
      {imgs.length > 0 && (
        <div
          className="mb-6 flex justify-center relative"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <div
            className="w-full max-w-4xl overflow-hidden rounded-lg shadow relative"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            aria-roledescription="carousel"
          >
            {/* Slides wrapper */}
            <div
              className="flex transition-transform duration-700"
              style={{
                width: `${imgs.length * 100}%`,
                transform: `translateX(-${(current * 100) / imgs.length}%)`,
              }}
            >
              {imgs.map((src, idx) => (
                <div
                  key={idx}
                  className="w-full flex-shrink-0 flex justify-center items-center"
                  style={{ width: `${100 / imgs.length}%` }}
                >
                  <img
                    src={src}
                    alt={`${title} ${idx + 1}`}
                    className="max-h-96 object-contain w-full"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>

            {/* Dot indicators */}
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
              {imgs.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goTo(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                  className={`w-3 h-2 rounded-full ${idx === current ? "bg-indigo-400" : "bg-gray-300"}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}


      {/* Technical Specifications */}
      {specs.length > 0 && (
        <section className="mb-8 ">
          <h2 className="text-2xl font-semibold mb-3">🔧 Technical Specifications</h2>
          <div className="overflow-x-auto">
            <table className="table-auto w-full border border-gray-300 text-sm md:text-base">
              <tbody>
                {specs.map(([key, value]) => (
                  <tr key={key} className="border-b">
                      <td className="border px-3 py-2 font-medium bg-gray-100">{key}</td>
                      <td className="border px-3 py-2">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Key Features */}
      {features.length > 0 && (
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">✨ Key Features</h2>
          <ul className="list-disc pl-6 space-y-2">
            {features.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Applications */}
      {applications && (
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">🧩 Applications</h2>
          <p>{applications}</p>
        </section>
      )}

      {/* Contact Us CTA */}
      {contactCta && (
        <section className="mt-10 mb-10 text-center">
          <h2 className="text-2xl font-semibold mb-4">📞 Contact Us Today</h2>
          <p className="text-lg mb-4">{contactCta}</p>
          <Link to="/contact-us" className="mt-2 rounded-md bg-indigo-600 px-3 py-2 text-xl font-medium text-white shadow-sm hover:bg-indigo-500">
            Enquiry Now
          </Link>
        </section>
      )}
    </div>
  );
};

export default ProductSection;
